
import type { SelectChangeEvent } from '@mui/material'
import Decimal from 'decimal.js'
import { type ChangeEvent, useCallback, useContext, useMemo, useState } from 'react'

import useNemoWallet from '@/hooks/use-nemo-wallet'
import useTrans from '@/hooks/useTrans'
import { toDecimalSafe } from '@/utils/numeric'
import { useEventCallback } from '@/utils/use-event-callback'

import { AssetWithdrawalContext } from '../context'
import type { IWithdrawalSubmitFn, NetworkOption } from '../types'
import View from './view'

const Container = ({ onSubmit }: { onSubmit: IWithdrawalSubmitFn }) => {
  const {
    selectedToken,
    setSelectedToken,
    currentTokenBalance,
    tokenOptions,
    networkOptions,
    isLoading,
    isSubmitSucceeded,
  } = useContext(AssetWithdrawalContext)
  const trans = useTrans()
  const { publicKey } = useNemoWallet()

  const [toWalletAddress, setToWalletAddress] = useState('')
  const [isNetworkMenuOpened, setIsNetworkMenuOpened] = useState(false)
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkOption | null>(null)
  const [inputAmount, setInputAmount] = useState<number>()

  const inValidInputAmountMsg = useMemo(() => {
    if (!selectedToken) return trans.asset_withdrawal.issues.no_token_info
    if (!toWalletAddress) return trans.asset_withdrawal.issues.enter_wallet_address

    const isMissingInputAmount = !inputAmount || new Decimal(inputAmount).lessThanOrEqualTo(0)
    if (isMissingInputAmount) return trans.asset_withdrawal.issues.enter_amount

    const inputAmountDecimalNumber = new Decimal(inputAmount)
    const isExceededBalance = inputAmountDecimalNumber.greaterThan(currentTokenBalance)
    const isInsufficientBalance = currentTokenBalance === 0
    if (isExceededBalance || isInsufficientBalance) return trans.asset_withdrawal.issues.insufficient_balance

    return ''
  }, [
    trans,
    currentTokenBalance,
    inputAmount,
    selectedToken,
    toWalletAddress,
  ])

  const submitButtonVisibility = useMemo(() => {
    if (!publicKey) {
      return {
        disabled: false,
        title: trans.common.connect_wallet,
      }
    }

    if (inValidInputAmountMsg) {
      return {
        disabled: true,
        invalidInput: true,
        title: inValidInputAmountMsg,
      }
    }

    return {
      disabled: false,
      title: trans.asset_withdrawal.withdraw_token(selectedToken?.displaySymbol ?? ''),
    }
  }, [publicKey, inValidInputAmountMsg, trans, selectedToken])

  const closeButtonVisibility = useMemo(() => {
    if (isSubmitSucceeded) {
      return {
        disabled: false,
        title: trans.asset_withdrawal.btn_close,
        click: () => setSelectedToken(null),
      }
    } else {
      return {
        disabled: isLoading,
        title: trans.common.back,
        click: () => setSelectedToken(null),
      }
    }
  }, [trans, isLoading, setSelectedToken, isSubmitSucceeded])

  const handleInputMaximumBalance = useCallback(() => {
    if (isLoading || !selectedToken || !currentTokenBalance) return

    const newInputAmount = toDecimalSafe(currentTokenBalance, selectedToken.decimals)

    if (newInputAmount.isFinite()) {
      setInputAmount(newInputAmount.toNumber())
    }
  }, [isLoading, selectedToken, currentTokenBalance, setInputAmount])

  const handleSubmit = useCallback(async () => {
    if (!inputAmount) return
    await onSubmit(toWalletAddress, inputAmount)
  }, [onSubmit, toWalletAddress, inputAmount])

  const onInputAmountChange = useEventCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) return

    const newInputAmount = Number(e.target.value)

    if (!Number.isNaN(newInputAmount)) setInputAmount(newInputAmount)
  })

  const onChangeToken = useCallback((evt: SelectChangeEvent<string>) => {
    setSelectedToken(tokenOptions.find(token => token.displaySymbol === evt.target.value) || null)
  }, [tokenOptions, setSelectedToken])

  const onChangeNetwork = useCallback((network: NetworkOption) => {
    setSelectedNetwork(network)
    setIsNetworkMenuOpened(false)
  }, [])

  if (!selectedToken) {
    return null
  }

  const injectedProps = {
    selectedToken,
    selectedNetwork,
    onChangeToken,
    isNetworkMenuOpened,
    setIsNetworkMenuOpened,
    networkOptions,
    tokenOptions,
    onChangeNetwork,
    toWalletAddress,
    setToWalletAddress,
    onInputAmountChange,
    inputAmount,
    isSubmitSucceeded,
    isLoading,
    submitButtonVisibility,
    closeButtonVisibility,
    currentTokenBalance,
    handleInputMaximumBalance,
    handleSubmit,
  }

  return (
    <View {...injectedProps} />
  )
}

export default Container
